import type {
    DropdownData,
    FilteredValue,
    RepeatableSubField,
    RepeatableSubFieldDataSource,
    RepeatableSubFieldDataSourceContext,
    RepeatableSubFieldDataSourceKeySelector,
} from '../../../interfaces/input.interface';
import type { RepeatableLogger } from './repeatable-group.types';

const DEFAULT_REPEATABLE_SUBFIELD_TIMEOUT_MS = 30000;
const DEFAULT_REPEATABLE_SUBFIELD_TTL_MS = 300000;

type RepeatableDataSourceEndpoint = {
    readonly toPromise?: (params?: Record<string, unknown>) => Promise<unknown>;
    readonly perform?: (params?: Record<string, unknown>) => unknown;
    readonly response?: (callback: (value: unknown) => void) => unknown;
    readonly getRequestData?: () => unknown;
    readonly hasError?: boolean;
};

type RepeatableDataSourceCacheEntry = {
    readonly expiresAt: number;
    readonly data: DropdownData<unknown>;
};

/**
 * Loads declarative data sources for repeatable sub-fields.
 *
 * This intentionally mirrors the top-level dropdown dataSource contract while staying scoped to a single
 * repeatable component instance. That avoids cross-form cache leakage and keeps row-aware mappings local.
 */
export class RepeatableSubFieldDataSourceLoader {
    private readonly cache = new Map<string, RepeatableDataSourceCacheEntry>();

    public constructor(private readonly logger: RepeatableLogger) {}

    public async load(subField: RepeatableSubField, context: RepeatableSubFieldDataSourceContext): Promise<DropdownData<unknown> | null> {
        const dataSource = subField.dataSource;

        if (!dataSource) {
            return null;
        }

        const cacheKey = this.buildCacheKey(subField, context, dataSource);
        const cached = this.resolveCachedData(cacheKey, dataSource);

        if (cached) {
            return this.cloneDropdownData(cached);
        }

        try {
            const response = await this.fetchDataSource(dataSource);
            const mappedData = this.mapResponse(subField, dataSource, response, context);

            if (mappedData.values.length > 0 || dataSource.cacheEmptyResults === true) {
                this.cache.set(cacheKey, {
                    expiresAt: Date.now() + (dataSource.ttlMs ?? DEFAULT_REPEATABLE_SUBFIELD_TTL_MS),
                    data: this.cloneDropdownData(mappedData),
                });
            }

            return mappedData;
        } catch (error: unknown) {
            this.logger.warn('Repeatable dataSource failed. Keeping existing options.', {
                key: subField.key,
                cacheKey,
                error,
            });

            return null;
        }
    }

    public clear(): void {
        this.cache.clear();
    }

    private resolveCachedData(
        cacheKey: string,
        dataSource: RepeatableSubFieldDataSource,
    ): DropdownData<unknown> | null {
        if (dataSource.forceReload === true) {
            return null;
        }

        const cached = this.cache.get(cacheKey);

        if (!cached) {
            return null;
        }

        if (cached.expiresAt < Date.now()) {
            this.cache.delete(cacheKey);

            return null;
        }

        return cached.data;
    }

    private buildCacheKey(
        subField: RepeatableSubField,
        context: RepeatableSubFieldDataSourceContext,
        dataSource: RepeatableSubFieldDataSource,
    ): string {
        return [subField.key, dataSource.cacheKey, context.rowIndex].join(':');
    }

    private async fetchDataSource(dataSource: RepeatableSubFieldDataSource): Promise<unknown> {
        const endpoint = dataSource.endpoint;
        let requestPromise: Promise<unknown>;

        if (typeof endpoint === 'function') {
            requestPromise = Promise.resolve(endpoint({}));
        } else if (this.isObservableLike(endpoint)) {
            requestPromise = this.fetchObservableData(endpoint);
        } else {
            requestPromise = this.fetchRequestHandlerData(endpoint);
        }

        return this.withTimeout(requestPromise, dataSource.timeoutMs);
    }

    private async fetchRequestHandlerData(endpoint: unknown): Promise<unknown> {
        const requestEndpoint = endpoint as RepeatableDataSourceEndpoint | null;

        if (!requestEndpoint) {
            throw new Error('Unsupported repeatable dataSource endpoint.');
        }

        if (typeof requestEndpoint.toPromise === 'function') {
            return requestEndpoint.toPromise({});
        }

        if (typeof requestEndpoint.perform === 'function' && typeof requestEndpoint.response === 'function') {
            return new Promise<unknown>((resolve, reject): void => {
                requestEndpoint.response?.((response: unknown): void => {
                    if (requestEndpoint.hasError === true) {
                        reject(new Error('Repeatable dataSource request finished with error.'));

                        return;
                    }

                    resolve(response);
                });

                requestEndpoint.perform?.({});
            });
        }

        if (typeof requestEndpoint.getRequestData === 'function') {
            return Promise.resolve(requestEndpoint.getRequestData());
        }

        throw new Error('Unsupported repeatable dataSource endpoint.');
    }


    private fetchObservableData(endpoint: { subscribe: unknown }): Promise<unknown> {
        return new Promise<unknown>((resolve, reject): void => {
            const subscribe = endpoint.subscribe as (observer: {
                next: (value: unknown) => void;
                error: (error: unknown) => void;
            }) => unknown;

            subscribe({
                next: (value: unknown): void => resolve(value),
                error: (error: unknown): void => reject(error),
            });
        });
    }

    private mapResponse(
        subField: RepeatableSubField,
        dataSource: RepeatableSubFieldDataSource,
        response: unknown,
        context: RepeatableSubFieldDataSourceContext,
    ): DropdownData<unknown> {
        const mappedResponse = dataSource.mapResponse
            ? dataSource.mapResponse(response, context)
            : this.pickResponsePayload(response, dataSource.responsePath);

        if (this.isDropdownData(mappedResponse)) {
            const values = this.normalizeOptions(subField, dataSource, mappedResponse.values, context);
            const filteredSource = mappedResponse.filtered.length > 0 ? mappedResponse.filtered : values;

            return {
                values,
                filtered: this.normalizeOptions(subField, dataSource, filteredSource, context),
                ...(mappedResponse.label !== undefined ? { label: mappedResponse.label } : {}),
            };
        }

        const values = this.normalizeOptions(subField, dataSource, mappedResponse, context);

        return { values, filtered: values };
    }

    private normalizeOptions(
        subField: RepeatableSubField,
        dataSource: RepeatableSubFieldDataSource,
        items: unknown,
        context: RepeatableSubFieldDataSourceContext,
    ): Array<FilteredValue<unknown>> {
        const values = Array.isArray(items) ? items : [];
        const mappedOptions = values.map((item) => this.mapOption(subField, dataSource, item, context));

        return this.deduplicateOptions(dataSource, mappedOptions, context);
    }

    private mapOption(
        subField: RepeatableSubField,
        dataSource: RepeatableSubFieldDataSource,
        item: unknown,
        context: RepeatableSubFieldDataSourceContext,
    ): FilteredValue<unknown> {
        if (dataSource.mapOption) {
            return dataSource.mapOption(item, context) as FilteredValue<unknown>;
        }

        if (item !== null && typeof item === 'object') {
            const record = item as Record<string, unknown>;
            const hasNativeOptionShape = 'value' in record && 'label' in record;
            const label = this.resolveLabel(subField, dataSource, record);
            const value = this.resolveValue(dataSource, record, item, hasNativeOptionShape);

            return {
                ...this.pickOptionMetadata(record),
                label,
                value,
                raw: record['raw'] ?? item,
            };
        }

        return {
            label: String(item ?? ''),
            value: item,
        };
    }

    private resolveLabel(
        subField: RepeatableSubField,
        dataSource: RepeatableSubFieldDataSource,
        record: Record<string, unknown>,
    ): string {
        const labelKeys = [
            dataSource.labelKey,
            ...(dataSource.labelKeys ?? []),
            subField.input?.optionLabel,
            'label',
            'nombre',
            'name',
            'razon_social',
            'descripcion',
            'codigo',
            'id',
        ].filter((key): key is string => typeof key === 'string' && key.trim() !== '');

        for (const key of labelKeys) {
            const value = record[key];

            if (value !== null && value !== undefined && String(value).trim() !== '') {
                return String(value);
            }
        }

        return '';
    }

    private resolveValue(
        dataSource: RepeatableSubFieldDataSource,
        record: Record<string, unknown>,
        item: unknown,
        hasNativeOptionShape: boolean,
    ): unknown {
        const value = this.readFirstNonEmptyRecordValue(record, [
            dataSource.valueKey,
            ...(dataSource.valueKeys ?? []),
        ]);

        if (value !== null) {
            return value;
        }

        return hasNativeOptionShape ? record['value'] : item;
    }

    private readFirstNonEmptyRecordValue(record: Record<string, unknown>, keys: readonly (string | undefined)[]): unknown | null {
        for (const key of keys) {
            if (!key || !(key in record)) {
                continue;
            }

            const value = record[key];

            if (value !== null && value !== undefined && String(value).trim() !== '') {
                return value;
            }
        }

        return null;
    }

    private deduplicateOptions(
        dataSource: RepeatableSubFieldDataSource,
        options: Array<FilteredValue<unknown>>,
        context: RepeatableSubFieldDataSourceContext,
    ): Array<FilteredValue<unknown>> {
        if (!dataSource.distinctBy) {
            return options;
        }

        const seen = new Set<string>();
        const result: Array<FilteredValue<unknown>> = [];

        for (const option of options) {
            const key = this.normalizeDistinctKey(this.resolveDistinctKey(dataSource.distinctBy, option, context));

            if (key === '' || seen.has(key)) {
                continue;
            }

            seen.add(key);
            result.push(option);
        }

        return result;
    }

    private resolveDistinctKey(
        distinctBy: RepeatableSubFieldDataSourceKeySelector,
        option: FilteredValue<unknown>,
        context: RepeatableSubFieldDataSourceContext,
    ): unknown {
        const rawValue = option.raw ?? option.value ?? option;

        if (typeof distinctBy === 'function') {
            return distinctBy(rawValue, context);
        }

        const record = rawValue !== null && typeof rawValue === 'object'
            ? rawValue as Record<string, unknown>
            : option as unknown as Record<string, unknown>;

        if (typeof distinctBy === 'string') {
            return record[distinctBy];
        }

        return distinctBy.map((key) => record[key]).join(':');
    }

    private pickOptionMetadata(record: Record<string, unknown>): Record<string, unknown> {
        const metadata: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(record)) {
            if (key === 'value' || key === 'label') {
                continue;
            }

            if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
                metadata[key] = value;
            }
        }

        return metadata;
    }

    private pickResponsePayload(response: unknown, responsePath: string | undefined): unknown {
        if (responsePath) {
            return this.readResponsePath(response, responsePath);
        }

        if (Array.isArray(response)) {
            return response;
        }

        if (response !== null && typeof response === 'object') {
            const record = response as Record<string, unknown>;

            if (Array.isArray(record['data'])) {
                return record['data'];
            }

            if (Array.isArray(record['items'])) {
                return record['items'];
            }

            if (Array.isArray(record['results'])) {
                return record['results'];
            }
        }

        return [];
    }

    private readResponsePath(response: unknown, path: string): unknown {
        return path.split('.').reduce<unknown>((current, key) => {
            if (current !== null && typeof current === 'object') {
                return (current as Record<string, unknown>)[key];
            }

            return undefined;
        }, response);
    }

    private isDropdownData(value: unknown): value is DropdownData<unknown> {
        if (value === null || typeof value !== 'object') {
            return false;
        }

        const record = value as Partial<DropdownData<unknown>>;

        return Array.isArray(record.values) && Array.isArray(record.filtered);
    }

    private cloneDropdownData(data: DropdownData<unknown>): DropdownData<unknown> {
        return {
            values: [...data.values],
            filtered: [...data.filtered],
            ...(data.label !== undefined ? { label: data.label } : {}),
        };
    }

    private normalizeDistinctKey(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private isObservableLike(value: unknown): value is { subscribe: unknown } {
        return value !== null && typeof value === 'object' && typeof (value as { subscribe?: unknown }).subscribe === 'function';
    }

    private async withTimeout(requestPromise: Promise<unknown>, timeoutMs: number | undefined): Promise<unknown> {
        const resolvedTimeout = timeoutMs ?? DEFAULT_REPEATABLE_SUBFIELD_TIMEOUT_MS;

        if (resolvedTimeout <= 0) {
            return requestPromise;
        }

        return new Promise<unknown>((resolve, reject): void => {
            const timeoutId = globalThis.setTimeout((): void => {
                reject(new Error('Repeatable dataSource request timed out.'));
            }, resolvedTimeout);

            requestPromise
                .then((value) => {
                    globalThis.clearTimeout(timeoutId);
                    resolve(value);
                })
                .catch((error: unknown) => {
                    globalThis.clearTimeout(timeoutId);
                    reject(error);
                });
        });
    }
}
